import { useState } from "react";
import { Check, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackContactSubmit } from "@/lib/analytics";
import { SERVICES, PROPERTY_TYPES, COMPANY } from "@/data/site";

const CONTACT_METHODS = ["Phone", "Email", "WhatsApp"];
const BEDROOMS = ["1", "2", "3", "4", "5+"];
const input = "w-full border border-[var(--border)] px-4 py-3 text-sm text-[var(--ink)] focus:outline-none focus:border-[var(--oxblood)] transition-colors bg-white";
const Field = ({ label, children }) => (
  <div><label className="block text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] mb-2">{label}</label>{children}</div>
);

export default function LeadForm() {
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", property_category: "Residential", property_type: "", bedrooms: "",
    services: [], notes: "", photo_urls: [], preferred_contact: "Phone", source: "contact", website: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (name) => set("services", form.services.includes(name) ? form.services.filter((s) => s !== name) : [...form.services, name]);

  const uploadPhotos = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      set("photo_urls", [...form.photo_urls, ...urls]);
      toast.success(`${urls.length} photo(s) uploaded`);
    } catch { toast.error("Photo upload failed."); } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address) return toast.error("Please fill in your contact details");
    if (form.services.length === 0) return toast.error("Please select at least one service");
    setSubmitting(true);
    try {
      await api.post("/quotes", form);
      trackContactSubmit();
      setDone(true);
    } catch { toast.error("Sorry, we couldn't submit your request just now. Please call us on " + COMPANY.phone + " or try again shortly."); } finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="text-center py-16 px-8" data-testid="lead-success">
      <div className="w-16 h-16 rounded-full bg-[var(--oxblood)] text-white flex items-center justify-center mx-auto mb-6"><Check className="w-8 h-8" /></div>
      <h3 className="font-serif text-3xl mb-3">Thank you, {form.name.split(" ")[0]}.</h3>
      <p className="text-[var(--muted)]">Thank you — we've received your request. A member of the StoneCrest team will be in touch.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="lead-form">
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={(e) => set("website", e.target.value)} className="absolute left-[-9999px] w-0 h-0 opacity-0" />
      <div>
        <p className="text-[11px] uppercase tracking-[0.3em] text-[var(--oxblood)] mb-2">Free Quote</p>
        <h3 className="font-serif text-3xl md:text-4xl">Request your quote</h3>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full Name *"><input data-testid="quote-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={input} placeholder="Jane Smith" /></Field>
        <Field label="Phone Number *"><input data-testid="quote-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={input} placeholder="07…" /></Field>
        <Field label="Email *"><input data-testid="quote-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={input} placeholder="you@email.com" /></Field>
        <Field label="Property Address"><input data-testid="quote-address" value={form.address} onChange={(e) => set("address", e.target.value)} className={input} placeholder="Street, Town, Postcode" /></Field>
      </div>

      <Field label="Is this for a residential or commercial property? *">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          {["Residential", "Commercial"].map((c) => (
            <button
              type="button"
              key={c}
              data-testid={`property-category-${c.toLowerCase()}`}
              onClick={() => setForm((f) => ({ ...f, property_category: c, ...(c === "Commercial" ? { property_type: "", bedrooms: "" } : {}) }))}
              className={`py-3 text-sm rounded-full border transition-colors ${form.property_category === c ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </Field>

      {form.property_category === "Residential" && (
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Property Type">
            <div className="flex flex-wrap gap-2">
              {PROPERTY_TYPES.map((t) => (
                <button type="button" key={t} data-testid={`property-${t.toLowerCase()}`} onClick={() => set("property_type", t)} className={`px-4 py-2 text-sm rounded-full border transition-colors ${form.property_type === t ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>{t}</button>
              ))}
            </div>
          </Field>
          <Field label="Bedrooms">
            <div className="flex flex-wrap gap-2">
              {BEDROOMS.map((b) => (
                <button type="button" key={b} onClick={() => set("bedrooms", b)} className={`w-10 h-10 text-sm rounded-full border transition-colors ${form.bedrooms === b ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>{b}</button>
              ))}
            </div>
          </Field>
        </div>
      )}

      <Field label="Services Required *">
        <div className="grid sm:grid-cols-2 gap-3">
          {SERVICES.map((s) => (
            <button type="button" key={s.slug} data-testid={`service-check-${s.slug}`} onClick={() => toggleService(s.name)} className={`flex items-center gap-3 p-3.5 text-left text-sm border transition-colors ${form.services.includes(s.name) ? "border-[var(--oxblood)] bg-[var(--oxblood-light)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>
              <span className={`w-5 h-5 border flex items-center justify-center shrink-0 ${form.services.includes(s.name) ? "bg-[var(--oxblood)] border-[var(--oxblood)]" : "border-[var(--border)]"}`}>{form.services.includes(s.name) && <Check className="w-3.5 h-3.5 text-white" />}</span>
              {s.name}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Additional Notes"><textarea data-testid="quote-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className={input} placeholder="Tell us anything that will help us prepare your quote…" /></Field>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Photo Upload (optional)">
          <label className="flex items-center justify-center gap-2 border border-dashed border-[var(--border)] py-3 cursor-pointer hover:border-[var(--oxblood)] transition-colors text-sm text-[var(--muted)]">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[var(--oxblood)]" /> : <Upload className="w-4 h-4 text-[var(--oxblood)]" />}
            {uploading ? "Uploading…" : "Upload a photo of your property"}
            <input type="file" data-testid="quote-photo-upload" accept="image/*" multiple hidden onChange={(e) => e.target.files.length && uploadPhotos(Array.from(e.target.files))} />
          </label>
          {form.photo_urls.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {form.photo_urls.map((u, i) => (
                <div key={i} className="relative w-12 h-12 bg-[var(--bone)] flex items-center justify-center">
                  <Check className="w-4 h-4 text-[var(--oxblood)]" />
                  <button type="button" onClick={() => set("photo_urls", form.photo_urls.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-[var(--ink)] text-white rounded-full w-4 h-4 flex items-center justify-center"><X className="w-2.5 h-2.5" /></button>
                </div>
              ))}
            </div>
          )}
        </Field>
        <Field label="Preferred Contact Method">
          <div className="flex gap-2">
            {CONTACT_METHODS.map((m) => (
              <button type="button" key={m} onClick={() => set("preferred_contact", m)} className={`flex-1 py-2.5 text-sm rounded-full border transition-colors ${form.preferred_contact === m ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] hover:border-[var(--oxblood)]"}`}>{m}</button>
            ))}
          </div>
        </Field>
      </div>

      <button type="submit" data-testid="quote-submit" disabled={submitting} className="bg-[var(--oxblood)] text-white px-10 py-4 rounded-full tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors disabled:opacity-50 flex items-center gap-2">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Request My Free Quote"}
      </button>
    </form>
  );
}
